import {Alert, Button, Checkbox, Form, Space} from "antd";
import {useRef, useState} from "react";
import _t from "../../lang/translate";
import {attemptAutoOffer, AttemptAutoOfferOptions} from "../../services/taxService";
import {OpenModalProps} from "../../types/modalTypes";
import assertFC from "../../utilities/assertFC";
import {renderErrorMessage} from "../messages/errorMessage";

export default function AttemptTaxAutoOffer<T extends object>({ onSuccess, onCancel, payload }: OpenModalProps<T>) {
  const [sendOffer, setSendOffer] = useState(false);
  const [allowInsecure, setAllowInsecure] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);
  const requiresAcknowledgment = sendOffer && allowInsecure;

  const handleSubmit = async () => {
    if (submitting.current || (requiresAcknowledgment && !acknowledged)) {
      return;
    }

    const options: AttemptAutoOfferOptions = { sendOffer, allowInsecure };
    submitting.current = true;
    setLoading(true);
    try {
      await attemptAutoOffer(payload.id, options);
      onSuccess(options as unknown as T);
    } catch (error) {
      renderErrorMessage(error);
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <Form requiredMark={false} layout="vertical" onFinish={handleSubmit}>
      <h2 className="modal-title">Generer automatisk tilbud</h2>
      <Form.Item>
        <Checkbox checked={sendOffer} disabled={loading} onChange={(event) => {
          setSendOffer(event.target.checked);
          setAcknowledged(false);
        }}>Send tilbud automatisk</Checkbox>
      </Form.Item>
      <Form.Item extra="Usikker generering omgår whitelist og, ved afsendelse, kontrollerne af maksimal nypris og at registreringsafgiften er nul. Nyprisen skal stadig være positiv, og øvrige valideringer gælder stadig.">
        <Checkbox checked={allowInsecure} disabled={loading} onChange={(event) => {
          setAllowInsecure(event.target.checked);
          setAcknowledged(false);
        }}>Tillad usikker generering</Checkbox>
      </Form.Item>
      {requiresAcknowledgment && (
        <>
          <Form.Item>
            <Alert type="warning" showIcon message="Du er ansvarlig for tilbuddet" description="Du sender tilbuddet uden de normale sikkerhedskontroller for whitelist, maksimal nypris og registreringsafgift. Du er ansvarlig for tilbuddets indhold og konsekvenserne af at sende det." />
          </Form.Item>
          <Form.Item>
            <Checkbox checked={acknowledged} disabled={loading} onChange={(event) => setAcknowledged(event.target.checked)}>
              Jeg accepterer ansvaret for at sende tilbuddet
            </Checkbox>
          </Form.Item>
        </>
      )}
      <div className="text-right">
        <Space>
          <Button type="ghost" onClick={() => onCancel()} disabled={loading}>{_t("cancel")}</Button>
          <Button htmlType="submit" type="primary" loading={loading} disabled={requiresAcknowledgment && !acknowledged}>
            Generer tilbud
          </Button>
        </Space>
      </div>
    </Form>
  );
}

assertFC(AttemptTaxAutoOffer);
